import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';

import Modal from '../../components/Modal';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { artifactsApi, ArtifactBundleDetailDTO, ArtifactRequestOptions } from '../../utils/artifactsApi';
import { requestErrorDetail, showRequestError } from '../../utils/requestError';
import { focusField } from '../../utils/artifactsUi';
import { RadioList } from '../../components/Filters/components/RadioList';
import { ScheduleDateTimeInput } from './ScheduleDateTimeInput';
import { ArtifactItemsEditor, DraftArtifactItem, toCreateItem, validateDrafts } from './ArtifactItemsEditor';
import { DemoAccessSection, DemoAccessState, emptyDemoAccess } from './DemoAccessSection';
import styles from './ArtifactsPage.module.scss';

type PublishMode = 'now' | 'draft' | 'schedule';

interface PublishBundleModalProps {
  isVisible: boolean;
  onClose: () => void;
  onPublished: (bundle: ArtifactBundleDetailDTO) => void;
  requestOptions?: ArtifactRequestOptions;
}

export function parseTags(value: string): string[] {
  return Array.from(new Set(value.split(',').map((tag) => tag.trim()).filter(Boolean)));
}

// datetime-local gives a wall-clock value in the browser zone; the API wants an aware instant
export function localDateTimeToIso(value: string): string {
  return new Date(value).toISOString();
}

export function isInFuture(localDateTime: string): boolean {
  return new Date(localDateTime).getTime() > Date.now();
}

export function PublishBundleModal({ isVisible, onClose, onPublished, requestOptions }: PublishBundleModalProps) {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  // mocked sharing UI shown at demos; nothing from it reaches the API yet
  const showSharing = Boolean(currentUser?.workspace?.config?.featureflags?.includes('show_artifact_sharing'));
  const [access, setAccess] = useState<DemoAccessState>(emptyDemoAccess);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [mode, setMode] = useState<PublishMode>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [items, setItems] = useState<DraftArtifactItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = useId();

  // Validation runs on submit, so a message would otherwise outlive the edit that fixes it
  useEffect(() => setError(null), [title, description, tags, mode, scheduledAt, items]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setTags('');
    setMode('now');
    setScheduledAt('');
    setItems([]);
    setAccess(emptyDemoAccess());
    setError(null);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const invalid = (message: string, fieldId: string) => {
    setError(message);
    focusField(fieldId);
  };

  const submit = async () => {
    if (!title.trim()) return invalid(t('A bundle needs a title'), `${ids}-title`);
    if (items.length === 0) return invalid(t('No artifacts added yet'), `${ids}-items`);
    const itemsError = validateDrafts(items, t);
    if (itemsError) return invalid(itemsError.message, itemsError.fieldId);
    if (mode === 'schedule' && (!scheduledAt || !isInFuture(scheduledAt))) {
      return invalid(t('The schedule must be in the future'), `${ids}-schedule`);
    }

    setBusy(true);
    setError(null);
    try {
      const bundle = await artifactsApi.publishBundle(
        {
          title: title.trim(),
          description: description.trim() || null,
          tags: parseTags(tags),
          status: mode === 'now' ? 'PUBLISHED' : 'DRAFT',
          scheduled_at: mode === 'schedule' ? localDateTimeToIso(scheduledAt) : null,
          artifacts: items.map(toCreateItem),
        },
        items.flatMap((item) => (item.file ? [item.file] : [])),
        requestOptions,
      );
      toast.success(mode === 'now' ? t('Bundle published') : t('Bundle scheduled'));
      if (showSharing && access.grants.length > 0) {
        toast.success(
          access.notify
            ? t('Shared with {{count}} people and sent the notification', { count: access.grants.length })
            : t('Shared with {{count}} people', { count: access.grants.length }),
        );
      }
      reset();
      onPublished(bundle);
    } catch (requestError) {
      showRequestError(requestError, t('Artifacts'));
      // the form stays open with its files, so keep the reason next to the button the
      // user is about to press again; editing any field clears it
      setError(requestErrorDetail(requestError) ?? t('The bundle could not be published'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      title={t('Publish bundle')}
      onClose={close}
      doNotCloseOnOutsideClick
      className={styles.publishModal}
      content={
        <div className={styles.formContent}>
          <div className="form-element">
            <label htmlFor={`${ids}-title`}>{t('Title')}</label>
            <input
              id={`${ids}-title`}
              type="text"
              placeholder={t('Bundle title') as string}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={busy}
              autoFocus
            />
          </div>
          <div className="form-element">
            <label htmlFor={`${ids}-description`}>{t('Description')}</label>
            <input
              id={`${ids}-description`}
              type="text"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={busy}
            />
          </div>
          <div className="form-element">
            <label htmlFor={`${ids}-tags`}>{t('Tags (comma separated)')}</label>
            <input
              id={`${ids}-tags`}
              type="text"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
              disabled={busy}
            />
          </div>

          <fieldset className={styles.modeGroup} disabled={busy}>
            <RadioList
              name="publish-mode"
              className={styles.modeRadios}
              selectedValue={mode}
              onChange={(value) => setMode(value as PublishMode)}
              items={[
                { name: t('Publish now'), value: 'now' },
                { name: t('Save as draft'), value: 'draft' },
                { name: t('Schedule for'), value: 'schedule' },
              ]}
            />
            <label htmlFor={`${ids}-schedule`} className={styles.visuallyHidden}>
              {t('Schedule for')}
            </label>
            <ScheduleDateTimeInput
              id={`${ids}-schedule`}
              value={scheduledAt}
              onChange={setScheduledAt}
              onFocus={() => setMode('schedule')}
            />
          </fieldset>

          <div id={`${ids}-items`} tabIndex={-1} className={styles.itemsSection}>
            <span className={styles.itemsTitle}>{t('Artifacts in the bundle')}</span>
            <ArtifactItemsEditor items={items} onChange={setItems} disabled={busy} />
          </div>

          {showSharing && <DemoAccessSection state={access} onChange={setAccess} bundleTitle={title} disabled={busy} />}

          {error && <div className={styles.formError}>{error}</div>}
        </div>
      }
      footer={
        <>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? t('Publishing...') : mode === 'now' ? t('Publish') : t('Save')}
          </button>
          <button className="secondary" onClick={close} disabled={busy}>
            {t('Cancel')}
          </button>
        </>
      }
    />
  );
}
